import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Plus, Trash2, Edit2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

export const SubscriptionManager = () => {
  const [tiers, setTiers] = useState<any[]>([]);
  const [editingTier, setEditingTier] = useState<any>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price_coins: 0,
    benefits: [] as string[],
  });
  const { toast } = useToast();

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('subscription_tiers')
      .select('*')
      .eq('creator_id', user.id)
      .order('price_coins', { ascending: true });

    if (!error && data) {
      setTiers(data);
    }
  };

  const saveTier = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const tierData = {
      ...formData,
      creator_id: user.id,
      benefits: formData.benefits,
    };

    if (editingTier) {
      const { error } = await supabase
        .from('subscription_tiers')
        .update(tierData)
        .eq('id', editingTier.id);

      if (!error) {
        toast({ title: 'Tier updated!' });
      }
    } else {
      const { error } = await supabase
        .from('subscription_tiers')
        .insert(tierData);

      if (!error) {
        toast({ title: 'Tier created!' });
      }
    }

    setFormData({ name: '', description: '', price_coins: 0, benefits: [] });
    setEditingTier(null);
    loadTiers();
  };

  const deleteTier = async (id: string) => {
    const { error } = await supabase
      .from('subscription_tiers')
      .delete()
      .eq('id', id);

    if (!error) {
      toast({ title: 'Tier deleted' });
      loadTiers();
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Subscription Tiers</CardTitle>
        <Dialog>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              New Tier
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingTier ? 'Edit' : 'Create'} Subscription Tier</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tier Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Gold Fan Club"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the benefits..."
                />
              </div>
              <div>
                <Label>Price (StageCoins)</Label>
                <Input
                  type="number"
                  value={formData.price_coins}
                  onChange={(e) => setFormData({ ...formData, price_coins: parseInt(e.target.value) })}
                />
              </div>
              <Button onClick={saveTier} className="w-full">
                {editingTier ? 'Update' : 'Create'} Tier
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {tiers.map((tier) => (
            <div key={tier.id} className="flex items-center justify-between p-3 border rounded">
              <div>
                <h4 className="font-semibold">{tier.name}</h4>
                <p className="text-sm text-muted-foreground">{tier.description}</p>
                <p className="text-sm font-medium mt-1">{tier.price_coins} coins/month</p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => {
                  setEditingTier(tier);
                  setFormData(tier);
                }}>
                  <Edit2 className="h-4 w-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => deleteTier(tier.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
